import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Calendar, ArrowRight, Clock, BookOpen } from "lucide-react";

// Placeholder blog posts — swap in real CMS data (or a /api/blogs endpoint) when ready.
const POSTS = [
  {
    slug: "ai-automation-roi",
    title: "The real ROI of AI Automation in 2026",
    excerpt: "How mid-market teams are getting back 1,200 hours a quarter with focused AI deployments — and where the easy wins still live.",
    cover: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=1400&q=80",
    tag: "AI Automation",
    readMins: 6,
    date: "Jan 8, 2026",
    author: "ZeroLabs Editorial",
  },
  {
    slug: "shipping-saas-faster",
    title: "Shipping enterprise SaaS faster without compromising security",
    excerpt: "Our default stack for building B2B SaaS that can pass SOC 2 and ISO checks while still going live in 12 weeks.",
    cover: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1400&q=80",
    tag: "Web Development",
    readMins: 8,
    date: "Dec 19, 2025",
    author: "Engineering Team",
  },
  {
    slug: "plant-3d-vs-e3d",
    title: "Plant 3D vs E3D vs S3D: a 2026 decision guide",
    excerpt: "When each platform actually wins on real industrial projects — pricing, learning curve, ecosystem, and hidden costs.",
    cover: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=1400&q=80",
    tag: "Plant Engineering",
    readMins: 11,
    date: "Dec 2, 2025",
    author: "Engineering Team",
  },
  {
    slug: "voice-agents-customer-support",
    title: "Voice agents are quietly replacing tier-1 customer support",
    excerpt: "What works, what doesn't, and how to scope a voice-first support layer that customers actually enjoy.",
    cover: "https://images.unsplash.com/photo-1589254065878-42c9da997008?auto=format&fit=crop&w=1400&q=80",
    tag: "AI Automation",
    readMins: 5,
    date: "Nov 15, 2025",
    author: "ZeroLabs Editorial",
  },
  {
    slug: "flutter-or-react-native",
    title: "Flutter or React Native? A founder's framework for 2026",
    excerpt: "We've shipped both for a decade. Here's a clean decision tree based on team, budget, and product surface.",
    cover: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1400&q=80",
    tag: "Mobile",
    readMins: 7,
    date: "Oct 28, 2025",
    author: "Mobile Team",
  },
  {
    slug: "rag-vs-fine-tuning",
    title: "RAG vs fine-tuning: pick one without regretting it",
    excerpt: "A practical lens for product teams who are tired of vendor pitches and want a clear answer.",
    cover: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1400&q=80",
    tag: "AI Automation",
    readMins: 9,
    date: "Oct 10, 2025",
    author: "AI Team",
  },
];

const TAGS = ["All", "AI Automation", "Web Development", "Mobile", "Plant Engineering"];

export default function Blogs() {
  useEffect(() => {
    document.title = "Insights & Articles — ZeroLabs Digitals";
  }, []);

  const [hero, ...rest] = POSTS;

  return (
    <div className="pt-24 pb-24" data-testid="page-blogs">
      <section className="relative overflow-hidden">
        <div className="orb orb-purple" style={{ top: "-100px", right: "-60px", width: "360px", height: "360px" }} />
        <div className="orb orb-orange" style={{ top: "120px", left: "-80px", width: "280px", height: "280px", opacity: 0.45 }} />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <div className="eyebrow">Insights & Articles</div>
          <h1 className="mt-3 text-4xl md:text-6xl font-extrabold tracking-tighter text-white leading-[1.05]">
            Notes from the <span className="text-gradient">build floor</span>.
          </h1>
          <p className="mt-4 text-[#B6B0C7] max-w-2xl">
            Deep dives, playbooks and field reports from our software, AI and plant-engineering teams.
            No fluff — only what we actually use to ship.
          </p>

          <div className="mt-8 flex flex-wrap gap-2">
            {TAGS.map((t) => (
              <button
                key={t}
                data-testid={`blogs-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-4 py-1.5 rounded-full text-xs font-semibold border border-white/10 text-[#B6B0C7] hover:border-[#22D3EE]/40 hover:text-white transition-colors"
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured post */}
      <section className="mt-12 max-w-7xl mx-auto px-6 md:px-10">
        <Link to={`/blogs/${hero.slug}`} className="card overflow-hidden block group" data-testid={`blog-featured-${hero.slug}`}>
          <div className="grid lg:grid-cols-12">
            <div className="lg:col-span-7 relative">
              <img src={hero.cover} alt={hero.title} className="w-full h-[360px] lg:h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0E0B17]/40 lg:to-transparent" />
              <div className="absolute top-4 left-4">
                <span className="px-2 py-1 rounded-md text-[10px] uppercase tracking-[0.2em] bg-[#3B82F6]/15 text-[#3B82F6] border border-[#3B82F6]/40">Featured</span>
              </div>
            </div>
            <div className="lg:col-span-5 p-8 flex flex-col justify-center">
              <div className="text-xs uppercase tracking-[0.22em] text-[#22D3EE] font-bold">{hero.tag}</div>
              <h2 className="mt-3 text-2xl md:text-3xl font-bold text-white tracking-tight group-hover:text-[#3B82F6] transition-colors">
                {hero.title}
              </h2>
              <p className="mt-4 text-[#B6B0C7] leading-relaxed">{hero.excerpt}</p>
              <div className="mt-6 flex items-center gap-4 text-xs text-[#8C859E]">
                <span className="inline-flex items-center gap-1"><Calendar size={12}/> {hero.date}</span>
                <span className="inline-flex items-center gap-1"><Clock size={12}/> {hero.readMins} min read</span>
                <span>by {hero.author}</span>
              </div>
              <div className="mt-6 inline-flex items-center gap-2 text-sm text-[#3B82F6] group-hover:text-[#22D3EE]">
                Read article <ArrowRight size={14}/>
              </div>
            </div>
          </div>
        </Link>
      </section>

      {/* Grid */}
      <section className="mt-14 max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((p) => (
            <Link
              key={p.slug}
              to={`/blogs/${p.slug}`}
              className="card overflow-hidden block group"
              data-testid={`blog-card-${p.slug}`}
            >
              <div className="relative">
                <img src={p.cover} alt={p.title} className="w-full h-48 object-cover"/>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0E0B17] via-transparent to-transparent"/>
                <div className="absolute top-3 left-3">
                  <span className="px-2 py-1 rounded-md text-[10px] uppercase tracking-[0.2em] bg-[#22D3EE]/15 text-[#22D3EE] border border-[#22D3EE]/40">{p.tag}</span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-white font-semibold text-lg leading-snug group-hover:text-[#3B82F6] transition-colors">{p.title}</h3>
                <p className="mt-2 text-[#B6B0C7] text-sm leading-relaxed line-clamp-3">{p.excerpt}</p>
                <div className="mt-4 flex items-center gap-3 text-xs text-[#8C859E]">
                  <span className="inline-flex items-center gap-1"><Calendar size={12}/> {p.date}</span>
                  <span className="inline-flex items-center gap-1"><Clock size={12}/> {p.readMins} min</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mt-20 max-w-5xl mx-auto px-6 md:px-10 text-center">
        <div className="card p-10 glow-border">
          <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-[#3B82F6]/30 to-[#22D3EE]/30 border border-white/10 text-white flex items-center justify-center">
            <BookOpen size={20}/>
          </div>
          <h3 className="mt-5 text-2xl md:text-3xl font-bold text-white">Get one practical insight a fortnight</h3>
          <p className="mt-3 text-[#B6B0C7] max-w-xl mx-auto">
            Short, opinionated notes on AI, software and plant engineering — straight from the team. No spam, ever.
          </p>
          <form onSubmit={(e) => e.preventDefault()} className="mt-6 flex gap-2 max-w-md mx-auto">
            <input data-testid="blogs-newsletter-email" type="email" placeholder="you@company.com" className="input-field flex-1"/>
            <button data-testid="blogs-newsletter-submit" className="btn-primary" type="submit">Subscribe</button>
          </form>
          <p className="mt-3 text-[10px] text-[#6F687F]">(Newsletter form is wired locally — we'll route it to Resend once the sender domain is verified.)</p>
        </div>
      </section>
    </div>
  );
}
